import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Button, Space, Radio, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { compressImage } from '@ubus/shared';

type Tool = 'crop' | 'redact';
type Rect = { x: number; y: number; w: number; h: number };

export type ImageEditModalProps = {
  open: boolean;
  file: File | null;
  onCancel: () => void;
  onConfirm: (file: File) => void;
};

const DISPLAY_MAX_W = 720;

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function normalizeRect(a: { x: number; y: number }, b: { x: number; y: number }): Rect {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return { x, y, w: Math.abs(b.x - a.x), h: Math.abs(b.y - a.y) };
}

function paintBase(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  width: number,
  height: number,
  redacts: Rect[]
) {
  ctx.clearRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);
  ctx.fillStyle = '#000';
  for (const r of redacts) {
    ctx.fillRect(r.x, r.y, r.w, r.h);
  }
}

/** Map a rect from display-canvas pixels → natural image pixels. */
function canvasRectToNatural(r: Rect, scale: number): Rect {
  const s = scale > 0 ? scale : 1;
  return {
    x: r.x / s,
    y: r.y / s,
    w: r.w / s,
    h: r.h / s,
  };
}

function fitCanvasToImage(
  canvas: HTMLCanvasElement,
  img: HTMLImageElement,
  maxW: number
): number {
  const scale = img.width > maxW ? maxW / img.width : 1;
  canvas.width = Math.max(1, Math.round(img.width * scale));
  canvas.height = Math.max(1, Math.round(img.height * scale));
  return scale;
}

/**
 * Crop + rectangular black redaction before upload (assign attachments).
 */
export default function ImageEditModal({ open, file, onCancel, onConfirm }: ImageEditModalProps) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  /** canvas_px = natural_px * displayScale */
  const displayScaleRef = useRef(1);
  const [tool, setTool] = useState<Tool>('crop');
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [dragCurrent, setDragCurrent] = useState<{ x: number; y: number } | null>(null);
  const [cropDraft, setCropDraft] = useState<Rect | null>(null);
  const [redacts, setRedacts] = useState<Rect[]>([]);
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    paintBase(ctx, img, canvas.width, canvas.height, redacts);

    const live =
      dragStart && dragCurrent ? normalizeRect(dragStart, dragCurrent) : null;
    const cropPreview = tool === 'crop' ? live ?? cropDraft : cropDraft;

    if (tool === 'crop' && cropPreview && cropPreview.w > 2 && cropPreview.h > 2) {
      const s = displayScaleRef.current || 1;
      const src = canvasRectToNatural(cropPreview, s);
      ctx.fillStyle = 'rgba(0,0,0,0.45)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      // Source = natural pixels; dest = canvas crop rect (fixes zoomed preview)
      ctx.drawImage(
        img,
        src.x,
        src.y,
        src.w,
        src.h,
        cropPreview.x,
        cropPreview.y,
        cropPreview.w,
        cropPreview.h
      );
      ctx.fillStyle = '#000';
      for (const r of redacts) {
        ctx.fillRect(r.x, r.y, r.w, r.h);
      }
      ctx.strokeStyle = '#2563EB';
      ctx.lineWidth = 2;
      ctx.strokeRect(cropPreview.x, cropPreview.y, cropPreview.w, cropPreview.h);
    }

    if (tool === 'redact' && live && live.w > 2 && live.h > 2) {
      ctx.fillStyle = 'rgba(0,0,0,0.7)';
      ctx.fillRect(live.x, live.y, live.w, live.h);
      ctx.strokeStyle = '#f59e0b';
      ctx.lineWidth = 1;
      ctx.strokeRect(live.x, live.y, live.w, live.h);
    }
  }, [cropDraft, dragCurrent, dragStart, redacts, tool]);

  useEffect(() => {
    if (!open || !file) {
      setReady(false);
      imgRef.current = null;
      displayScaleRef.current = 1;
      setCropDraft(null);
      setRedacts([]);
      setDragStart(null);
      setDragCurrent(null);
      setTool('crop');
      return;
    }

    let cancelled = false;
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      if (cancelled) {
        URL.revokeObjectURL(url);
        return;
      }
      URL.revokeObjectURL(url);
      imgRef.current = img;
      const canvas = canvasRef.current;
      if (!canvas) return;
      displayScaleRef.current = fitCanvasToImage(canvas, img, DISPLAY_MAX_W);
      setCropDraft(null);
      setRedacts([]);
      setReady(true);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      message.error(t('trips.messages.imageEditLoadFailed'));
    };
    img.src = url;

    return () => {
      cancelled = true;
    };
  }, [open, file, t]);

  useEffect(() => {
    if (ready) redraw();
  }, [ready, redraw]);

  const pointerPos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * canvas.width;
    const y = ((e.clientY - rect.top) / rect.height) * canvas.height;
    return {
      x: clamp(x, 0, canvas.width),
      y: clamp(y, 0, canvas.height),
    };
  };

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    const p = pointerPos(e);
    setDragStart(p);
    setDragCurrent(p);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!dragStart) return;
    setDragCurrent(pointerPos(e));
  };

  const onPointerUp = () => {
    if (!dragStart || !dragCurrent) {
      setDragStart(null);
      setDragCurrent(null);
      return;
    }
    const r = normalizeRect(dragStart, dragCurrent);
    if (r.w > 4 && r.h > 4) {
      if (tool === 'crop') setCropDraft(r);
      else setRedacts((prev) => [...prev, r]);
    }
    setDragStart(null);
    setDragCurrent(null);
  };

  const applyCrop = () => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    const crop = cropDraft;
    if (!canvas || !img || !crop || crop.w < 4 || crop.h < 4) {
      message.warning(t('trips.messages.imageEditCropFirst'));
      return;
    }

    const s = displayScaleRef.current || 1;
    const src = canvasRectToNatural(crop, s);

    const out = document.createElement('canvas');
    out.width = Math.max(1, Math.round(src.w));
    out.height = Math.max(1, Math.round(src.h));
    const ctx = out.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(img, src.x, src.y, src.w, src.h, 0, 0, out.width, out.height);

    // Redacts are in canvas space → natural relative to crop
    const nextRedactsNatural = redacts
      .map((r) => {
        const nr = canvasRectToNatural(r, s);
        return {
          x: nr.x - src.x,
          y: nr.y - src.y,
          w: nr.w,
          h: nr.h,
        };
      })
      .map((r) => ({
        x: clamp(r.x, 0, out.width),
        y: clamp(r.y, 0, out.height),
        w: Math.min(r.w, out.width - clamp(r.x, 0, out.width)),
        h: Math.min(r.h, out.height - clamp(r.y, 0, out.height)),
      }))
      .filter((r) => r.w > 1 && r.h > 1);

    for (const r of nextRedactsNatural) {
      ctx.fillStyle = '#000';
      ctx.fillRect(r.x, r.y, r.w, r.h);
    }

    const dataUrl = out.toDataURL('image/jpeg', 0.92);
    const nextImg = new Image();
    nextImg.onload = () => {
      imgRef.current = nextImg;
      const fit = fitCanvasToImage(canvas, nextImg, DISPLAY_MAX_W);
      displayScaleRef.current = fit;
      setCropDraft(null);
      // Keep redacts aligned with new display canvas
      setRedacts(
        nextRedactsNatural.map((r) => ({
          x: r.x * fit,
          y: r.y * fit,
          w: r.w * fit,
          h: r.h * fit,
        }))
      );
    };
    nextImg.src = dataUrl;
  };

  const exportCanvas = (img: HTMLImageElement, width: number, height: number, marks: Rect[]) => {
    const out = document.createElement('canvas');
    out.width = width;
    out.height = height;
    const ctx = out.getContext('2d');
    if (!ctx) return null;
    paintBase(ctx, img, width, height, marks);
    return out;
  };

  const handleConfirm = async () => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !file) return;

    setBusy(true);
    try {
      const s = displayScaleRef.current || 1;
      let bake: HTMLCanvasElement | null;

      if (cropDraft && cropDraft.w > 4 && cropDraft.h > 4) {
        const src = canvasRectToNatural(cropDraft, s);
        const w = Math.max(1, Math.round(src.w));
        const h = Math.max(1, Math.round(src.h));
        const mapped = redacts
          .map((r) => {
            const nr = canvasRectToNatural(r, s);
            return {
              x: nr.x - src.x,
              y: nr.y - src.y,
              w: nr.w,
              h: nr.h,
            };
          })
          .map((r) => ({
            x: clamp(r.x, 0, w),
            y: clamp(r.y, 0, h),
            w: Math.min(r.w, w - clamp(r.x, 0, w)),
            h: Math.min(r.h, h - clamp(r.y, 0, h)),
          }))
          .filter((r) => r.w > 1 && r.h > 1);
        const tmp = document.createElement('canvas');
        tmp.width = w;
        tmp.height = h;
        const ctx = tmp.getContext('2d');
        if (!ctx) throw new Error('canvas');
        ctx.drawImage(img, src.x, src.y, src.w, src.h, 0, 0, w, h);
        for (const r of mapped) {
          ctx.fillStyle = '#000';
          ctx.fillRect(r.x, r.y, r.w, r.h);
        }
        bake = tmp;
      } else {
        // Export full natural image with redacts mapped from canvas → natural
        const naturalMarks = redacts.map((r) => canvasRectToNatural(r, s));
        bake = exportCanvas(img, img.naturalWidth || img.width, img.naturalHeight || img.height, naturalMarks);
      }
      if (!bake) throw new Error('export');

      const blob = await new Promise<Blob | null>((resolve) =>
        bake!.toBlob((b) => resolve(b), 'image/jpeg', 0.92)
      );
      if (!blob) throw new Error('export');
      const baseName = file.name.replace(/\.\w+$/, '') || 'image';
      const exported = new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
      const compressed = await compressImage(exported, 1600, 0.85);
      onConfirm(compressed);
    } catch {
      message.error(t('trips.messages.imageEditFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      title={t('trips.imageEdit.title')}
      onCancel={onCancel}
      width={800}
      destroyOnClose
      footer={
        <Space wrap>
          <Button onClick={onCancel}>{t('common.cancel')}</Button>
          <Button
            disabled={!ready || redacts.length === 0}
            onClick={() => setRedacts((prev) => prev.slice(0, -1))}
          >
            {t('trips.imageEdit.undoRedact')}
          </Button>
          <Button disabled={!ready || !cropDraft} onClick={applyCrop}>
            {t('trips.imageEdit.applyCrop')}
          </Button>
          <Button type="primary" loading={busy} disabled={!ready} onClick={() => void handleConfirm()}>
            {t('trips.imageEdit.confirm')}
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Radio.Group
          value={tool}
          onChange={(e) => setTool(e.target.value as Tool)}
          optionType="button"
          buttonStyle="solid"
          options={[
            { value: 'crop', label: t('trips.imageEdit.toolCrop') },
            { value: 'redact', label: t('trips.imageEdit.toolRedact') },
          ]}
        />
        <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 13 }}>
          {tool === 'crop' ? t('trips.imageEdit.hintCrop') : t('trips.imageEdit.hintRedact')}
        </div>
        <div
          style={{
            overflow: 'auto',
            maxHeight: '60vh',
            background: '#f5f5f5',
            borderRadius: 8,
            textAlign: 'center',
            padding: 8,
          }}
        >
          <canvas
            ref={canvasRef}
            style={{
              maxWidth: '100%',
              cursor: 'crosshair',
              touchAction: 'none',
              background: '#fff',
            }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          />
        </div>
      </Space>
    </Modal>
  );
}
